"use client";

import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  CheckCircle,
  Calculator,
  TrendingUp,
  TrendingDown,
  Info,
  Loader2,
  FileText,
} from "lucide-react";
import React from "react";
import { Button } from "./ui/button";

// Helper function to parse date string (DD/MM/YY format)
const parseDate = (dateStr: string): Date => {
  const [day, month, year] = dateStr.split('/');
  // Convert 2-digit year to 4-digit (assuming 20xx)
  const fullYear = parseInt(year) < 50 ? 2000 + parseInt(year) : 1900 + parseInt(year);
  return new Date(fullYear, parseInt(month) - 1, parseInt(day));
};

// Helper function to find latest dates from JSON keys
const findLatestDates = (data: any, count: number = 4): string[] => {
  const dates = Object.keys(data).filter(key => {
    // Check if key matches date format DD/MM/YY
    return /^\d{2}\/\d{2}\/\d{2}$/.test(key);
  });
  
  if (dates.length === 0) return [];
  
  // Sort dates and return the latest N dates
  dates.sort((a, b) => {
    const dateA = parseDate(a);
    const dateB = parseDate(b);
    return dateB.getTime() - dateA.getTime();
  });
  
  return dates.slice(0, Math.min(count, dates.length));
};

interface FinancialRatio {
  name: string;
  key: string;
  value: number;
  formula: string;
  description: string;
  isPercentage: boolean;
  isDays: boolean;
  category: 'liquidity' | 'efficiency' | 'profitability';
}

interface FinancialData {
  formulae?: {
    Current_ratio?: number;
    Inventory_turnover_ratio?: number;
    Inventory_days?: number;
    Debtor_turnover_ratio?: number;
    Debtor_days?: number;
    Creditor_turnover_ratio?: number;
    Creditor_days?: number;
    Operating_cycle?: number;
    Net_profit_ratio?: number;
    Debt_equity_ratio?: number;
  };
}

interface RatioCardProps {
  ratio: FinancialRatio;
  chartData: { date: string; value: number }[];
  onLoad: () => void;
}

function RatioCard({ ratio, chartData, onLoad }: RatioCardProps) {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Simulate loading time for each chart
    const timer = setTimeout(
      () => {
        setIsLoaded(true);
        onLoad();
      },
      Math.random() * 500 + 200,
    );

    return () => clearTimeout(timer);
  }, [onLoad]);

  const latestValue = chartData[chartData.length - 1]?.value;
  const previousValue = chartData[chartData.length - 2]?.value;
  const trend =
    latestValue !== undefined && previousValue !== undefined
      ? latestValue > previousValue
        ? "up"
        : "down"
      : null;

  const formatValue = (value: number) => {
    if (ratio.isPercentage) {
      return `${(value * 100).toFixed(2)}%`;
    } else if (ratio.isDays) {
      return `${Math.round(value)} days`;
    } else {
      return value.toFixed(2);
    }
  };

  const formatTooltipValue = (value: number) => {
    if (ratio.isPercentage) {
      return `${(value * 100).toFixed(2)}%`;
    } else if (ratio.isDays) {
      return `${Math.round(value)} days`;
    } else {
      return value.toFixed(2);
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="rounded-lg border border-white/20 bg-black/90 p-3 text-white shadow-xl backdrop-blur-md">
          <p className="font-semibold text-sm">{`${ratio.name}`}</p>
          <p className="mb-1 text-xs text-white/60">{label}</p>
          <p className="text-base font-bold text-[#4da3ff]">
            {formatTooltipValue(data.value)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="h-full border-white/10 bg-white/5 backdrop-blur-sm">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">{ratio.name}</CardTitle>
          {trend && (
            <Badge
              variant={trend === "up" ? "default" : "destructive"}
              className="border-white/10 text-xs"
            >
              {trend === "up" ? (
                <TrendingUp className="mr-1 h-3 w-3" />
              ) : (
                <TrendingDown className="mr-1 h-3 w-3" />
              )}
              {trend === "up" ? "Up" : "Down"}
            </Badge>
          )}
        </div>
        <CardDescription className="text-xs text-white/60">
          {ratio.description}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-2xl font-bold text-white">
              {latestValue !== undefined ? formatValue(latestValue) : "N/A"}
            </span>
            <div className="flex items-center text-xs text-white/60">
              <Info className="mr-1 h-3 w-3" />
              {ratio.formula}
            </div>
          </div>

          <div className="h-56">
            {isLoaded && chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis
                    dataKey="date"
                    fontSize={11}
                    stroke="rgba(255,255,255,0.6)"
                    tickLine={false}
                  />
                  <YAxis
                    fontSize={11}
                    stroke="rgba(255,255,255,0.6)"
                    tickLine={false}
                    tickFormatter={(value) =>
                      ratio.isPercentage
                        ? `${(value * 100).toFixed(0)}%`
                        : ratio.isDays
                          ? `${value}d`
                          : value.toFixed(1)
                    }
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#4da3ff"
                    strokeWidth={2.5}
                    dot={{ fill: "#4da3ff", r: 4 }}
                    activeDot={{ r: 6, fill: "#4da3ff" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center">
                <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-[#4da3ff]"></div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Define all ratios with their metadata
const RATIO_DEFINITIONS: Omit<FinancialRatio, 'value'>[] = [
  {
    name: "Current Ratio",
    key: "Current_ratio",
    formula: "Current Assets / Current Liabilities",
    description: "Measures short-term liquidity",
    isPercentage: false,
    isDays: false,
    category: 'liquidity',
  },
  {
    name: "Inventory Turnover Ratio",
    key: "Inventory_turnover_ratio",
    formula: "Cost of Goods Sold / Average Inventory",
    description: "How efficiently inventory is managed",
    isPercentage: false,
    isDays: false,
    category: 'efficiency',
  },
  {
    name: "Inventory Days",
    key: "Inventory_days",
    formula: "365 / Inventory Turnover Ratio",
    description: "Days to sell inventory",
    isPercentage: false,
    isDays: true,
    category: 'liquidity',
  },
  {
    name: "Debtor Turnover Ratio",
    key: "Debtor_turnover_ratio",
    formula: "Net Credit Sales / Average Receivables",
    description: "How efficiently receivables are collected",
    isPercentage: false,
    isDays: false,
    category: 'efficiency',
  },
  {
    name: "Debtor Days",
    key: "Debtor_days",
    formula: "365 / Debtor Turnover Ratio",
    description: "Average collection period",
    isPercentage: false,
    isDays: true,
    category: 'liquidity',
  },
  {
    name: "Creditor Turnover Ratio",
    key: "Creditor_turnover_ratio",
    formula: "Purchases / Average Payables",
    description: "How efficiently payables are managed",
    isPercentage: false,
    isDays: false,
    category: 'efficiency',
  },
  {
    name: "Creditor Days",
    key: "Creditor_days",
    formula: "365 / Creditor Turnover Ratio",
    description: "Average payment period",
    isPercentage: false,
    isDays: true,
    category: 'efficiency',
  },
  {
    name: "Operating Cycle",
    key: "Operating_cycle",
    formula: "Inventory Days + Debtor Days",
    description: "Time to convert inventory to cash",
    isPercentage: false,
    isDays: true,
    category: 'efficiency',
  },
  {
    name: "Net Profit Ratio",
    key: "Net_profit_ratio",
    formula: "Net Profit / Revenue",
    description: "Profitability percentage",
    isPercentage: true,
    isDays: false,
    category: 'profitability',
  },
  {
    name: "Debt-Equity Ratio",
    key: "Debt_equity_ratio",
    formula: "Total Debt / Shareholder Equity",
    description: "Financial leverage indicator",
    isPercentage: false,
    isDays: false,
    category: 'profitability',
  },
];

export function FinancialRatiosSection() {
  const [loadedRatios, setLoadedRatios] = useState(0);
  const [allRatios, setAllRatios] = useState<FinancialRatio[]>([]);
  const [chartData, setChartData] = useState<{
    [key: string]: { date: string; value: number }[];
  }>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [latestDate, setLatestDate] = useState<string | null>(null);
  const [numberOfDates, setNumberOfDates] = useState<number>(0);

  // Load financial data from SharePoint
  const loadFinancialData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading Financial Ratios data');

      // Download FinData_Formulae.json from SharePoint
      if ((window as any).sharePointAPI?.downloadClientJson) {
        const result = await (window as any).sharePointAPI.downloadClientJson("FinData_Formulae.json");
        
        if (result?.success && result.data) {
          const data = result.data;
          
          // Find latest dates (up to 4)
          const dates = findLatestDates(data, 4);
          if (dates.length === 0) {
            throw new Error("No valid dates found in financial data");
          }
          
          const latest = dates[0];
          setLatestDate(latest);
          setNumberOfDates(dates.length);
          
          // Build ratios from latest date
          const latestData = data[latest] as FinancialData;
          const ratios: FinancialRatio[] = RATIO_DEFINITIONS.map(def => ({
            ...def,
            value: latestData?.formulae?.[def.key as keyof typeof latestData.formulae] || 0,
          }));
          
          // Build chart data for all dates (reverse order so latest is on the right)
          const chartDataMap: { [key: string]: { date: string; value: number }[] } = {};
          const reversedDates = [...dates].reverse(); // Reverse to show oldest on left, latest on right
          
          RATIO_DEFINITIONS.forEach(def => {
            chartDataMap[def.name] = reversedDates.map(date => {
              const dateData = data[date] as FinancialData;
              const value = dateData?.formulae?.[def.key as keyof typeof dateData.formulae] || 0;
              return {
                date,
                value,
              };
            });
          });
          
          setAllRatios(ratios);
          setChartData(chartDataMap);
          setDataLoaded(true);
          console.log('Financial Ratios data loaded successfully for dates:', dates);
        } else {
          throw new Error(result?.error || "Failed to download financial data");
        }
      } else {
        throw new Error("SharePoint API not available");
      }
    } catch (err) {
      console.error('Failed to load Financial Ratios data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleRatioLoad = () => {
    setLoadedRatios((prev) => prev + 1);
  };

  const totalRatios = 10; // Hardcoded as per requirements
  const isAllLoaded = loadedRatios === totalRatios;

  // Auto-load data when component mounts
  useEffect(() => {
    loadFinancialData();
  }, []);

  // Show load button if data hasn't been loaded yet
  if (!dataLoaded && !loading) {
    return (
      <div className="flex h-full items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <h2 className="text-3xl font-bold text-white mb-6">Financial Ratios</h2>
          <p className="text-white/80 text-lg">Close Trial Balance for these pages to load</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-white" />
          <span className="text-white">Loading Financial Ratios data</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-400 mb-2">Error loading data</div>
          <div className="text-sm text-white/60 mb-4">{error}</div>
          <Button 
            onClick={loadFinancialData}
            className="mt-4 bg-[#4da3ff] hover:bg-[#3a8bcc] text-white"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-white">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold tracking-tight">
              Financial Ratios
            </h2>
            {isAllLoaded && (
              <CheckCircle className="h-6 w-6 text-emerald-400" />
            )}
          </div>
          <p className="text-white/60">
            Key financial ratios with year-over-year comparison
          </p>
          {latestDate && (
            <p className="text-sm text-white/50 mt-1">
              Latest data: {latestDate}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className="border-white/10 text-xs text-white/80"
          >
            {loadedRatios}/{totalRatios} Loaded
          </Badge>
          <Calculator className="h-5 w-5 text-white/60" />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="border-white/10 bg-white/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Ratios</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">10</div>
            <p className="text-xs text-white/60">
              Financial performance indicators
            </p>
          </CardContent>
        </Card>

        <Card className="border-white/10 bg-white/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              Years Compared
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">{numberOfDates}</div>
            <p className="text-xs text-white/60">Years of historical data</p>
          </CardContent>
        </Card>
      </div>

      {/* Ratios Grid */}
      <div className="grid gap-4 md:grid-cols-2">
        {allRatios.map((ratio) => (
          <RatioCard
            key={ratio.name}
            ratio={ratio}
            chartData={chartData[ratio.name] || []}
            onLoad={handleRatioLoad}
          />
        ))}
      </div>

      {/* Additional Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Ratio Interpretation Guide</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <h4 className="mb-2 font-semibold">Liquidity Ratios</h4>
              <ul className="space-y-1 text-sm text-white/60">
                <li>
                  • <strong>Current Ratio:</strong> Should be above 1.0 for good
                  liquidity
                </li>
                <li>
                  • <strong>Inventory Days:</strong> Lower is better for
                  efficiency
                </li>
                <li>
                  • <strong>Debtor Days:</strong> Shorter collection period is
                  preferred
                </li>
              </ul>
            </div>
            <div>
              <h4 className="mb-2 font-semibold">Efficiency Ratios</h4>
              <ul className="space-y-1 text-sm text-white/60">
                <li>
                  • <strong>Turnover Ratios:</strong> Higher values indicate
                  better efficiency
                </li>
                <li>
                  • <strong>Operating Cycle:</strong> Shorter cycle means faster
                  cash conversion
                </li>
                <li>
                  • <strong>Net Profit Ratio:</strong> Higher percentage shows
                  better profitability
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
